import { useEffect, useState } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import isMobile from 'src/_shared/utils/isMobile';

/**
 * Hook to show an overlay when the device is in landscape mode.
 * This is intended for **mobile web only**. It does nothing on native apps.
 *
 * It has to be used with LandscapeOverlay.tsx component.
 *
 * Returns `true` if the overlay should be shown (landscape on mobile web).
 * Returns `false` otherwise.
 */
export default function useIsOrientationLocked() {
  const [showOverlay, setShowOverlay] = useState(false);

  useEffect(() => {
    // If we're not running on web (e.g., running in a native RN app),
    // the overlay is never shown. Early return to avoid doing anything else.
    if (Platform.OS !== 'web') {
      setShowOverlay(false);
      return;
    }

    /**
     * Function that checks if we're on a mobile device and, if so,
     * sets state based on whether we're in a landscape orientation.
     */
    const checkOrientation = () => {
      
      if (isMobile()) {
        // Check if the browser supports the Screen Orientation API:
        // 'screen.orientation.type' can be one of:
        // 'portrait-primary', 'portrait-secondary', 'landscape-primary', or 'landscape-secondary'.
        if (screen.orientation && screen.orientation.type) {
          // If the orientation type string starts with "landscape",
          // it means the device is currently in landscape mode.
          setShowOverlay(screen.orientation.type.startsWith('landscape'));
        } else {
          // Fallback: if 'screen.orientation' or 'screen.orientation.type'
          // is not available (older browsers), we can do a naive check:
          // if width is greater than height, assume landscape.
          setShowOverlay(window.innerWidth > window.innerHeight);
        }
      } else {
        // If not mobile, we never show the overlay.
        setShowOverlay(false);
      }
    };

    // Perform the initial orientation check.
    checkOrientation();

    // Listen for changes to the device orientation using the Screen Orientation API
    // if it's available. Otherwise, fall back to listening for window resize events.
    if (screen.orientation && screen.orientation.addEventListener) {
      // Modern browsers that fully support the Screen Orientation API
      screen.orientation.addEventListener('change', checkOrientation);
    } else {
      // Fallback for older browsers that do not support 'screen.orientation.addEventListener'
      // or do not implement the ScreenOrientation interface at all.
      window.addEventListener('resize', checkOrientation);
    }

    // Cleanup function to remove event listeners when the component unmounts.
    return () => {
      if (screen.orientation && screen.orientation.removeEventListener) {
        screen.orientation.removeEventListener('change', checkOrientation);
      } else {
        window.removeEventListener('resize', checkOrientation);
      }
    };
  }, []);

  // Return whether we should show the overlay or not.
  return showOverlay;
}
